// payments (catalog-outline.md "payments"): the tender aggregate of @radish/core/payments on the wire (stream
// `tender:<id>`). The Owl Club's card flow is auth-and-tip: a pre-auth approved for the check, the tip keyed from the
// slip after close, captured with the tip in the 02:00 batch; a card held on a bar tab is a `preauth` until charged.
// Event payloads decode to exactly core's `TenderEvent` shapes (minus `type`).
import { z } from 'zod';
import { defineCommand } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { defineEvent } from '../../define/event.ts';
import { additive } from '../../define/upcast.ts';
import { secret } from '../../define/secret.ts';
import { CheckId, CustomerId, EmployeeId, EpochMs, IsoInstant, LocationId, Money, OrderId, TenderId, TenderTypeId, TillSessionId } from '../../primitives/codecs.ts';

/** `gift_card` (lane D2): a gift-card redemption the cloud ledger approved first (L8, L19). */
const TenderMethod = z.enum(['cash', 'card', 'other', 'gift_card']).meta({ id: 'TenderMethod' });
const ResponseOrigin = z.enum(['online', 'saf']).meta({ id: 'ResponseOrigin' });
const ReversalStatus = z.enum(['requested', 'confirmed', 'not_required']).meta({ id: 'ReversalStatus' });
const Card = z.object({ cardBrand: z.string().min(1).max(32), last4: z.string().regex(/^\d{4}$/) }).meta({ id: 'CardDetails' });

export const TenderSnapshot = z
  .object({
    tenderId: TenderId,
    orderId: OrderId,
    status: z.enum([
      'started', 'declined', 'authorized', 'preauth', 'charged', 'capture_pending', 'captured', 'capture_failed', 'voided',
      'declined_post_forward',
    ]),
    method: TenderMethod,
    requested: Money,
    authorized: Money.nullable(),
    /** Applied to the order, ex tip. */
    applied: Money.nullable(),
    tip: Money.nullable(),
    version: z.int().nonnegative(),
  })
  .meta({ id: 'TenderSnapshot' });

// ---- errors ----------------------------------------------------------------------------------------------------------
export const TenderNotFound = defineError({
  code: 'payments.not_found', httpStatus: 404, message: 'errors.payments.not_found', retryable: false,
  details: z.object({ tenderId: TenderId }),
});
export const OrderNotPayable = defineError({
  code: 'payments.order_not_payable', httpStatus: 409, message: 'errors.payments.order_not_payable', retryable: false,
  details: z.object({ orderStatus: z.string() }),
});
export const TenderTillNotOpen = defineError({
  code: 'payments.till_not_open', httpStatus: 409, message: 'errors.payments.till_not_open', retryable: false,
  details: z.object({ tillSessionId: TillSessionId }),
});
/** Any other rule core's tender machine refuses (`rule` is core's error code, e.g. `negative_tip`). */
export const TenderRuleViolation = defineError({
  code: 'payments.rule_violation', httpStatus: 409, message: 'errors.payments.rule_violation', retryable: false,
  details: z.object({ rule: z.string() }),
});

// ---- events ----------------------------------------------------------------------------------------------------------
const tender = { aggregate: 'tender', deviceConsumed: true } as const;

/**
 * What an `other` tender is (W-44): a check and its number, a custom tender type (`catalog.tender_type`) with the
 * extra details it asked for, or (lane D2b, W-70) a charge to the house account of the order's customer. `pricedAs`:
 * True Margin takes a check at the cash price; a house charge is taken at the card price (legacy `cardDue`).
 */
export const OtherTender = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('check'), checkNumber: z.string().max(64), pricedAs: z.enum(['cash', 'card']) }),
    z.object({
      kind: z.literal('tender_type'), tenderTypeId: TenderTypeId, name: z.string().min(1).max(255), details: z.string().max(255).optional(), pricedAs: z.literal('card'),
    }),
    z.object({ kind: z.literal('house_account'), customerId: CustomerId, pricedAs: z.literal('card') }),
  ])
  .meta({ id: 'OtherTender' });

const TenderStartedPayload = z.object({
  tenderId: TenderId, orderId: OrderId, method: TenderMethod, requested: Money, tillSessionId: TillSessionId,
  collectedByEmployeeId: EmployeeId, startedAt: EpochMs,
  /** The check it pays on a split order. */
  checkId: CheckId.optional(),
  other: OtherTender.optional(),
});

/** A v5 payload as a v4 reader takes it: a house-account charge is an `other` tender it cannot name (no `other`). */
const startedWithoutHouseAccount = (newer: unknown): unknown => {
  const p = newer as { readonly other?: { readonly kind?: unknown } };
  if (p.other?.kind !== 'house_account') return newer;
  return Object.fromEntries(Object.entries(p).filter(([key]) => key !== 'other'));
};

export const TenderStarted = defineEvent({
  ...tender,
  name: 'TenderStarted',
  version: 5,
  summary: 'A payment was started on an order (on one of its checks, when split), on a till session.',
  payload: TenderStartedPayload,
  verifiable: ['tenderId', 'orderId', 'method', 'requested', 'tillSessionId', 'other'],
  merge: 'lifecycle',
  // v2 added the check it pays (a v1 tender pays the order); v3 what an `other` tender is (check, custom tender type); v4
  // (lane D2) the gift_card method; v5 (lane D2b) the house-account charge. Each step only widened the payload, so every
  // older payload is a valid next one. A v4 register reads a house charge as an unnamed `other` tender of the same amount.
  upcastFrom: [additive(1), { version: 2, up: (old) => old as z.input<typeof TenderStartedPayload> }, additive(3), additive(4)],
  downcastTo: [{ version: 4, down: startedWithoutHouseAccount }],
});

/**
 * B1469: an order's stored tax and total (legacy `orders.tax`, `orders.total`) as the payment that makes a tender count
 * writes them, worked out by the paying register. The order's stored figures read them back instead of repricing, so a
 * pricing change after the payment never moves them. An older register's payment carries none and is repriced as before.
 */
const OrderStoredFigures = z.object({ tax: Money, total: Money });

const TenderAuthorizedPayload = z.object({
  authorized: Money, authCode: z.string().max(32).optional(), gatewayRef: z.string().max(128).optional(), responseOrigin: ResponseOrigin,
  isPreAuth: z.boolean(), card: Card.optional(),
  /**
   * The Non-Cash Adjustment fee inside `authorized` (legacy `tenders.fee` on a card, with `amount` the rest): core's
   * program amount for this card tender. Absent on other programs and before v2.
   */
  surcharge: Money.optional(),
  /**
   * Legacy's tender ticket number (`tenders.ticket` = "{drawer letter}-{drawer.ticket}", the drawer's counter bumped once
   * per payment recorded): the next on the tender's till session, assigned by the register holding the till when the
   * payment is taken. Absent before v3. Nothing keys on it: two tenders may share one (a server bank on two registers
   * both cut off from the node), and are told apart by their ids.
   */
  ticketNumber: z.int().positive().optional(),
  /** B1469: the order's stored figures this payment writes (core `storedTotalsAtPayment`); absent before v4 and on a hold. */
  orderStored: OrderStoredFigures.optional(),
});

/** A payload as a reader of an earlier version takes it: the named field dropped. */
const without = (field: string) => (newer: unknown): unknown =>
  Object.fromEntries(Object.entries(newer as Record<string, unknown>).filter(([key]) => key !== field));
const withoutSurcharge = without('surcharge');

export const TenderAuthorized = defineEvent({
  ...tender,
  name: 'TenderAuthorized',
  version: 4,
  summary: 'The processor approved a card: auth-and-tip, a straight sale, or a bar-tab pre-auth hold.',
  payload: TenderAuthorizedPayload,
  verifiable: ['authorized', 'isPreAuth', 'surcharge', 'orderStored'],
  merge: 'lifecycle',
  pii: ['card.last4'],
  // v2 (lane D2) added the Non-Cash Adjustment fee inside the amount; a v1 authorization carries none. v3 (lane D2) added
  // the ticket number; a v2 reader drops it and shows the order number, as it always has. v4 (lane RG, B1469) the order's
  // stored figures; a v3 reader drops them and reprices the order, as it always has.
  upcastFrom: [additive(1), additive(2), additive(3)],
  downcastTo: [{ version: 3, down: without('orderStored') }, { version: 2, down: without('ticketNumber') }, { version: 1, down: withoutSurcharge }],
});

const TenderIncrementedPayload = z.object({ newAuthorized: Money, gatewayRef: z.string().max(128), requested: Money.optional() });

export const TenderIncremented = defineEvent({
  ...tender,
  name: 'TenderIncremented',
  version: 2,
  summary: 'A bar-tab hold was raised or lowered to a new authorized amount (below `requested` when only partly approved).',
  payload: TenderIncrementedPayload,
  verifiable: ['newAuthorized', 'requested'],
  merge: { exclusive: 'tenderId' },
  // v2 added what the register asked for; a v1 increment was approved in full.
  upcastFrom: [{ version: 1, up: (old) => old as z.input<typeof TenderIncrementedPayload> }],
});

const TenderCapturedPayload = z.object({
  amount: Money, tip: Money, cashDiscount: Money, cashDiscountTax: Money.optional(), tendered: Money.optional(), change: Money.optional(),
  changeRounding: Money.optional(),
  captureRef: z.string().max(128).optional(),
  /** The Non-Cash Adjustment fee inside `amount` (legacy `tenders.fee` on a card); absent on other programs and before v4. */
  surcharge: Money.optional(),
  /** B1469: the order's stored figures a hold's charge writes (ChargeAttachedCard); absent before v5 and elsewhere. */
  orderStored: OrderStoredFigures.optional(),
});

export const TenderCaptured = defineEvent({
  ...tender,
  name: 'TenderCaptured',
  version: 5,
  summary: 'The payment settled: cash taken, a charged hold, or a card captured with its tip.',
  payload: TenderCapturedPayload,
  verifiable: ['amount', 'tip', 'cashDiscount', 'cashDiscountTax', 'change', 'changeRounding', 'surcharge', 'orderStored'],
  merge: 'lifecycle',
  // v2 added the tax a True Margin cash discount moved (`cashDiscountTax`); v3 the cash change rounding
  // (`changeRounding`); v4 (lane D2) the Non-Cash Adjustment fee inside a card's amount (`surcharge`). An older capture
  // has none of them; a v3 reader takes a v4 capture without its surcharge. v5 (lane RG, B1469) the order's stored figures
  // a hold's charge writes; a v4 reader drops them and reprices the order.
  upcastFrom: [additive(1), { version: 2, up: (old) => old as z.input<typeof TenderCapturedPayload> }, additive(3), additive(4)],
  downcastTo: [{ version: 4, down: without('orderStored') }, { version: 3, down: withoutSurcharge }],
});

export const TenderDetached = defineEvent({
  ...tender,
  name: 'TenderDetached',
  version: 2,
  summary: 'A bar-tab hold was detached from the tab without charging it.',
  payload: z.object({
    /** B1657: the order's stored figures the detach writes (the hold starts counting as paid); absent before v2. */
    orderStored: OrderStoredFigures.optional(),
  }),
  verifiable: ['orderStored'],
  merge: 'lifecycle',
  // v2 (lane RG, B1657) the order's stored figures the detach writes; a v1 reader drops them and reprices the order.
  upcastFrom: [additive(1)],
  downcastTo: [{ version: 1, down: without('orderStored') }],
});

export const HoldMoved = defineEvent({
  ...tender,
  name: 'HoldMoved',
  version: 1,
  summary: "A bar-tab hold that was not charged followed its order to the order's till session after a transfer.",
  payload: z.object({ tillSessionId: TillSessionId, fromTillSessionId: TillSessionId }),
  verifiable: ['tillSessionId', 'fromTillSessionId'],
  merge: 'lifecycle',
});

export const TipAdjusted = defineEvent({
  ...tender,
  name: 'TipAdjusted',
  version: 1,
  summary: 'The tip on a card was keyed or changed (from the paper slip).',
  payload: z.object({ tip: Money, previousTip: Money, adjustedByEmployeeId: EmployeeId.optional() }),
  verifiable: ['tip'],
  merge: { exclusive: 'tenderId' },
});

export const TenderCaptureRequested = defineEvent({
  ...tender,
  name: 'TenderCaptureRequested',
  version: 1,
  summary: 'The batch asked the processor to capture a card for its amount and tip (write-ahead).',
  payload: z.object({ attemptId: z.string().min(1).max(64), amount: Money, tip: Money }),
  verifiable: ['amount', 'tip'],
  merge: 'lifecycle',
});

export const TipFinalized = defineEvent({
  ...tender,
  name: 'TipFinalized',
  version: 1,
  summary: 'The tip a capture settled with.',
  payload: z.object({ tip: Money }),
  verifiable: ['tip'],
  merge: 'lifecycle',
});

export const TenderCaptureFailed = defineEvent({
  ...tender,
  name: 'TenderCaptureFailed',
  version: 1,
  summary: 'The processor refused a capture.',
  payload: z.object({ attemptId: z.string().min(1).max(64), responseCode: z.string().max(32) }),
  verifiable: [],
  merge: 'lifecycle',
});

export const TenderVoided = defineEvent({
  ...tender,
  name: 'TenderVoided',
  version: 1,
  summary: 'A payment was voided (a hold released, a card reversed, cash returned).',
  payload: z.object({ reason: z.string().min(1).max(500), reversalStatus: ReversalStatus, voidedByEmployeeId: EmployeeId.optional() }),
  // B1687 (lane RG): whether a card reversal was confirmed is money, and it comes from the command. Not voidedByEmployeeId:
  // core records the approver there when there is one (`approvedBy ?? acting`), re-verification decides without one, and
  // SEC-60 judges it as the authorizer (override.ts AUTHORIZER_FIELDS).
  verifiable: ['reversalStatus'],
  merge: 'lifecycle',
});

export const TenderDeclined = defineEvent({
  ...tender,
  name: 'TenderDeclined',
  version: 2,
  summary: 'The processor declined a card.',
  payload: z.object({
    responseCode: z.string().max(32), message: z.string().max(500),
    /**
     * B1840: the manager whose PIN approved declining it (an unconfirmed card's "It was not charged" or "Void it and
     * charge again", CD-T13). Absent on a plain decline. SEC-60 judges it as the approver (override.ts APPROVER_FIELDS).
     */
    approvedBy: EmployeeId.optional(),
  }),
  verifiable: [],
  merge: 'lifecycle',
  // v2 added the approving manager, absent from every v1 decline; a v1 reader gets the decline without it.
  upcastFrom: [additive(1)],
  downcastTo: [{ version: 1, down: without('approvedBy') }],
});

export const TenderForwarded = defineEvent({
  ...tender,
  name: 'TenderForwarded',
  version: 1,
  summary: 'A store-and-forward approval was forwarded and approved.',
  payload: z.object({ gatewayRef: z.string().max(128) }),
  verifiable: [],
  merge: 'lifecycle',
});

export const TenderDeclinedPostForward = defineEvent({
  ...tender,
  name: 'TenderDeclinedPostForward',
  version: 1,
  summary: 'A store-and-forward approval was declined when forwarded.',
  payload: z.object({ responseCode: z.string().max(32) }),
  verifiable: [],
  merge: 'lifecycle',
});

/** Every event the tender aggregate records, in the order core's `TenderEvent` union lists them. */
export const tenderEvents = [
  TenderStarted, TenderAuthorized, TenderIncremented, TenderCaptured, TenderDetached, TipAdjusted, TenderCaptureRequested, TipFinalized,
  TenderCaptureFailed, TenderVoided, TenderDeclined, TenderForwarded, TenderDeclinedPostForward, HoldMoved,
] as const;

// ---- commands --------------------------------------------------------------------------------------------------------
const onTender = {
  availability: 'location', origin: 'device', stream: { aggregate: 'tender', id: (input: { tenderId: string }) => input.tenderId },
} as const;
const quiet = { risk: 'low', dryRun: false, agent: { expose: false } } as const;
const staff = { permission: { session: 'staff' }, output: TenderSnapshot } as const;
const tenderErrors = [TenderNotFound, TenderRuleViolation] as const;

export const StartTender = defineCommand({
  ...onTender, ...quiet, ...staff,
  name: 'payments.startTender',
  summary: 'Start a payment on an order on an open till session (cash, card, or other).',
  input: z.object({
    tenderId: TenderId, orderId: OrderId, method: TenderMethod, requested: Money, tillSessionId: TillSessionId, checkId: CheckId.optional(),
    other: OtherTender.optional(),
  }),
  emits: [TenderStarted],
  errors: [OrderNotPayable, TenderTillNotOpen, TenderRuleViolation],
  legacy: ['POST /v1/orders/{id}/transactions'],
});

export const ApproveCard = defineCommand({
  ...onTender, ...quiet, ...staff,
  name: 'payments.approveCard',
  summary: 'Record the processor\'s approval of a card: auth-and-tip, sale, or a bar-tab pre-auth hold.',
  input: z.object({
    tenderId: TenderId, approval: z.enum(['auth_and_tip', 'sale', 'preauth']), authorized: Money, authCode: z.string().min(1).max(32),
    gatewayRef: z.string().min(1).max(128), responseOrigin: ResponseOrigin, card: Card,
    /** The Non-Cash Adjustment fee inside `authorized` (core's program amount for the tender); none on other programs. */
    surcharge: Money.optional(),
    /** B1469: the order's stored figures this payment writes (core `storedTotalsAtPayment`); ignored on a pre-auth hold. */
    orderStored: OrderStoredFigures.optional(),
  }),
  emits: [TenderAuthorized, TenderCaptured],
  errors: tenderErrors,
});

export const TakeCash = defineCommand({
  ...onTender, ...quiet, ...staff,
  name: 'payments.takeCash',
  summary:
    'Take cash (or another non-card tender): applied amount, the True Margin cash discount (and the tax it moved on a taxable order), and what was handed over.',
  input: z.object({
    tenderId: TenderId, applied: Money, cashDiscount: Money, cashDiscountTax: Money.optional(), tendered: Money.optional(),
    // Cash change rounding (location `cash.change_rounding`): exact change − change handed back, −4…+4 cents.
    changeRounding: Money.optional(),
    /** B1469: the order's stored figures this payment writes (core `storedTotalsAtPayment`). */
    orderStored: OrderStoredFigures.optional(),
  }),
  emits: [TenderAuthorized, TenderCaptured],
  errors: tenderErrors,
});

export const DeclineTender = defineCommand({
  ...onTender, ...quiet, ...staff,
  name: 'payments.declineTender',
  summary: 'Record a card decline.',
  input: z.object({ tenderId: TenderId, responseCode: z.string().min(1).max(32), message: z.string().max(500) }),
  emits: [TenderDeclined],
  errors: tenderErrors,
});

export const IncrementAuth = defineCommand({
  ...onTender, ...quiet, ...staff,
  name: 'payments.incrementAuth',
  summary: "Raise or lower a bar-tab hold to the check's card due (an incremental authorization or a partial reversal).",
  input: z.object({ tenderId: TenderId, newAuthorized: Money, gatewayRef: z.string().min(1).max(128), requested: Money.optional() }),
  emits: [TenderIncremented],
  errors: tenderErrors,
});

export const ChargeAttachedCard = defineCommand({
  ...onTender, ...quiet, ...staff,
  name: 'payments.chargeAttachedCard',
  summary: 'Charge a bar-tab hold for the check.',
  input: z.object({
    tenderId: TenderId, amount: Money, /** The Non-Cash Adjustment fee inside `amount`. */ surcharge: Money.optional(),
    /** B1469: the order's stored figures this charge writes (core `storedTotalsAtPayment`). */
    orderStored: OrderStoredFigures.optional(),
  }),
  emits: [TenderCaptured],
  errors: tenderErrors,
});

export const DetachCard = defineCommand({
  ...onTender, ...quiet, ...staff,
  name: 'payments.detachCard',
  summary: 'Detach a bar-tab hold from its tab without charging it.',
  input: z.object({
    tenderId: TenderId,
    /** B1657: the order's stored figures the detach writes (core `storedTotalsAtPayment`). */
    orderStored: OrderStoredFigures.optional(),
  }),
  emits: [TenderDetached],
  errors: tenderErrors,
});

export const MoveHold = defineCommand({
  ...onTender, ...quiet, ...staff,
  name: 'payments.moveHold',
  summary: "Move a bar-tab hold that was not charged to its order's till session, after the order was transferred.",
  input: z.object({ tenderId: TenderId }),
  emits: [HoldMoved],
  errors: tenderErrors,
});

export const AdjustTip = defineCommand({
  ...onTender, ...quiet, ...staff,
  name: 'payments.adjustTip',
  summary: 'Key or change the tip on an approved card before capture.',
  input: z.object({ tenderId: TenderId, tip: Money }),
  emits: [TipAdjusted],
  errors: tenderErrors,
  legacy: ['POST /v1/transactions/{id}/tip'],
});

export const RequestCapture = defineCommand({
  ...onTender, ...quiet, ...staff,
  name: 'payments.requestCapture',
  summary: 'Write-ahead record of a capture request (the 02:00 batch) for the card\'s amount and tip.',
  input: z.object({ tenderId: TenderId, attemptId: z.string().min(1).max(64) }),
  emits: [TenderCaptureRequested],
  errors: tenderErrors,
});

/**
 * B1666 (coordinator, 2026-10-29; ops plan 2026-10-29-settle-unconfirmed-refunds, Decided): a capture the processor
 * declined, tried again from the register (legacy Confirm tip "Retry {count} payments", and the next Finalize Tips).
 * Decided only by the node, under the tender's lock, so of two registers retrying one card exactly one is told to send
 * the capture; a register offline cannot retry. Refused unless the order is known not to have written the card off.
 * The acting employee rides in the input, as for `payments.voidTenderAtRegister`. The register then sends the capture
 * and records the processor's answer with `payments.recordCaptureResult` under the new attempt.
 */
export const RetryCaptureAtRegister = defineCommand({
  availability: 'location', origin: 'any', stream: onTender.stream, permission: { session: 'device' }, output: TenderSnapshot, ...quiet,
  name: 'payments.retryCaptureAtRegister',
  summary: 'Try again a card capture the processor declined (capture_failed): a new write-ahead capture attempt, decided by the node.',
  input: z.object({ tenderId: TenderId, attemptId: z.string().min(1).max(64), employeeId: EmployeeId, approvedByEmployeeId: EmployeeId.optional() }),
  emits: [TenderCaptureRequested],
  errors: tenderErrors,
  legacy: ['FinalizeAndRefundDialog.vue finalizeAll (Retry)', 'useTipsFinalization.ts finalizeTips (open tenders)'],
});

/**
 * B1936 (coordinator, 2026-11-01): Confirm tip on a card of an order this register does not hold (Order Lookup's
 * finalize-then-refund, legacy OrderRefundWithFinalizeButton): `payments.adjustTip` decided by the node, as the employee
 * named in the input (the approving manager's grants when one approved), as `payments.retryCaptureAtRegister` is.
 */
export const AdjustTipAtRegister = defineCommand({
  availability: 'location', origin: 'any', stream: onTender.stream, permission: { session: 'device' }, output: TenderSnapshot, ...quiet,
  name: 'payments.adjustTipAtRegister',
  summary: "payments.adjustTip for a card of an order this register does not hold, decided by the node as the acting employee.",
  input: AdjustTip.input.extend({ employeeId: EmployeeId, approvedByEmployeeId: EmployeeId.optional() }),
  emits: [TipAdjusted],
  errors: tenderErrors,
  legacy: ['FinalizeAndRefundDialog.vue updateTip (order lookup)', 'POST /v1/transactions/{id}/tip'],
});

/**
 * B1936: the write-ahead capture of that card with its tip, decided by the node under the tender's lock, so of two
 * registers finalizing one card exactly one is told to send the capture (the second is refused: the card is
 * capture_pending). The register then asks the processor and records its answer with
 * `payments.recordCaptureResultAtRegister` under this attempt.
 */
export const RequestCaptureAtRegister = defineCommand({
  availability: 'location', origin: 'any', stream: onTender.stream, permission: { session: 'device' }, output: TenderSnapshot, ...quiet,
  name: 'payments.requestCaptureAtRegister',
  summary: 'payments.requestCapture for a card of an order this register does not hold, decided by the node as the acting employee.',
  input: RequestCapture.input.extend({ employeeId: EmployeeId, approvedByEmployeeId: EmployeeId.optional() }),
  emits: [TenderCaptureRequested],
  errors: tenderErrors,
  legacy: ['FinalizeAndRefundDialog.vue finalizeAll (order lookup)'],
});

export const RecordCaptureResult = defineCommand({
  ...onTender, ...quiet, ...staff,
  name: 'payments.recordCaptureResult',
  summary: 'Record the processor\'s answer to a capture request.',
  input: z.object({
    tenderId: TenderId,
    attemptId: z.string().min(1).max(64),
    outcome: z.discriminatedUnion('approved', [
      z.object({ approved: z.literal(true), captureRef: z.string().min(1).max(128) }),
      z.object({ approved: z.literal(false), responseCode: z.string().min(1).max(32) }),
    ]),
  }),
  emits: [TipFinalized, TenderCaptured, TenderCaptureFailed],
  errors: tenderErrors,
});

/**
 * B1666: the processor's answer to a retried capture (`payments.retryCaptureAtRegister`), recorded by the node under the
 * attempt it decided, so the register needs not wait for that attempt to sync down. The register asked the capture under
 * the declined capture's own card operation id, so the cloud answers a capture the processor already took from its
 * journal, or looks a lost try up by its invoice, and never sends it twice (payments `card-operations.ts`).
 */
export const RecordCaptureResultAtRegister = defineCommand({
  availability: 'location', origin: 'any', stream: onTender.stream, permission: { session: 'device' }, output: TenderSnapshot, ...quiet,
  name: 'payments.recordCaptureResultAtRegister',
  summary: "Record the processor's answer to a retried card capture, at the node, under the attempt the node decided.",
  input: RecordCaptureResult.input.extend({ employeeId: EmployeeId, approvedByEmployeeId: EmployeeId.optional() }),
  emits: [TipFinalized, TenderCaptured, TenderCaptureFailed],
  errors: tenderErrors,
  legacy: ['FinalizeAndRefundDialog.vue finalizeAll (Retry)', 'useTipsFinalization.ts finalizeTips (open tenders)'],
});

export const VoidTender = defineCommand({
  ...onTender, ...staff,
  name: 'payments.voidTender',
  summary: 'Void a payment while its till session is open (an uncharged hold can always be released).',
  input: z.object({ tenderId: TenderId, reason: z.string().min(1).max(500), reversalStatus: ReversalStatus }),
  risk: 'medium',
  dryRun: true,
  agent: { expose: false },
  emits: [TenderVoided],
  errors: [TenderNotFound, TenderTillNotOpen, TenderRuleViolation],
  legacy: ['POST /v1/transactions/{id}/void'],
});

/**
 * payments.voidTender for a payment of an order this register does not hold (an order the legacy import brought over),
 * decided by the node (ops plan 2026-10-13-refund-imported-orders, slice 4). The register's link to its node is its
 * device session, so the acting employee rides in the input and the node checks that employee's own grants (the
 * approving manager's when one approved). A card is voided at the processor first (`payments.runCardOperation`).
 */
export const VoidTenderAtRegister = defineCommand({
  availability: 'location', origin: 'any', stream: onTender.stream, permission: { session: 'device' }, output: TenderSnapshot,
  name: 'payments.voidTenderAtRegister',
  summary: 'payments.voidTender for a payment of an order this register does not hold, decided by the node. The acting employee (or approving manager) needs the void permission.',
  input: VoidTender.input.extend({ employeeId: EmployeeId, approvedByEmployeeId: EmployeeId.optional() }),
  risk: 'medium',
  dryRun: true,
  agent: { expose: false },
  emits: [TenderVoided],
  errors: [TenderNotFound, TenderTillNotOpen, TenderRuleViolation],
  legacy: ['GET /v1/tenders/{id}/void'],
});

/**
 * An online order's card payment, recorded on the order `orders.recordOnlineSale` recorded (ops plan
 * 2026-10-13-online-orders-in-reports; legacy CreateTender): a captured card tender of the amount ex tip, with the tip,
 * on the order's till. The cloud charged the card and voids it on a reject, cancel or void; this only records that.
 * Decided on the node: a retry or a second register records one tender. The tender id is the online order id.
 */
export const RecordOnlineTender = defineCommand({
  availability: 'location', origin: 'any', stream: onTender.stream, ...quiet,
  name: 'payments.recordOnlineTender',
  summary: "Record an online order's card payment on its order (amount ex tip, and the tip), and void it when the online order was voided.",
  input: z.object({
    tenderId: TenderId,
    orderId: OrderId,
    amount: Money,
    tip: Money,
    card: Card.optional(),
    voidReason: z.string().min(1).max(500).optional(),
  }),
  output: TenderSnapshot,
  permission: { session: 'device' },
  emits: [TenderStarted, TenderAuthorized, TenderCaptured, TenderVoided],
  errors: [...tenderErrors, OrderNotPayable],
  legacy: ['CreateTender (online)', 'POST /v1/orders/{id}/void2 (online)'],
});

export const RecordSafForward = defineCommand({
  ...onTender, ...quiet, ...staff,
  name: 'payments.recordSafForward',
  summary: 'Record the outcome of forwarding a store-and-forward approval.',
  input: z.object({
    tenderId: TenderId,
    outcome: z.discriminatedUnion('approved', [
      z.object({ approved: z.literal(true), gatewayRef: z.string().min(1).max(128) }),
      z.object({ approved: z.literal(false), responseCode: z.string().min(1).max(32) }),
    ]),
  }),
  emits: [TenderForwarded, TenderDeclinedPostForward],
  errors: tenderErrors,
});

export * from './refunds.ts';
export * from './processor.ts';
export * from './signatures.ts';

/** An address a receipt may be emailed to: one address, as long as an address may be (checked where it is typed and again where it is sent). */
export const ReceiptEmailAddress = z.email().max(254);

/**
 * A payment already has its most receipt mails, or its location sent its most in the last hour (SEC-118d: legacy had no
 * limit, so a device could use Radish's sender as a relay). Nothing is queued.
 */
export const ReceiptMailLimitReached = defineError({
  code: 'payments.receipt_mail_limit', httpStatus: 429, message: 'errors.payments.receipt_mail_limit', retryable: false,
  details: z.object({ limit: z.enum(['per_payment', 'per_location_hour']) }),
});

/**
 * R22 Email Receipt (legacy `POST /v1/receipt/payments`, hardware.md §5.22): the customer receipt text the register
 * rendered for one payment, queued for one address (`payments.receipt_mails`), subject "Receipt from {location} for
 * ${amount}". The address and the text are the customer's: secret fields, so the command records no event and keeps
 * neither in its request fingerprint. Queued, never sent until the location's mail transport sends (A28).
 */
export const EmailReceipt = defineCommand({
  name: 'payments.emailReceipt',
  summary: "Email a payment's customer receipt to one address (queued until mail is set up).",
  input: z.object({
    locationId: LocationId,
    tenderId: TenderId,
    /** One address (legacy took any list, unchecked, and put them all in To: B147, B148). */
    recipient: secret(ReceiptEmailAddress),
    /** The receipt as the register rendered it (`emailReceiptText`); never empty (legacy skips an empty one). */
    receiptText: secret(z.string().min(1).max(20_000)),
  }),
  output: z.object({ queuedAt: IsoInstant }),
  // The register asks from its Checkout over the device's cloud link (legacy POST /v1/receipt/payments is a device route).
  permission: { session: 'device' },
  risk: 'low',
  availability: 'cloud',
  origin: 'any',
  cloudReason: 'Mail leaves through the cloud mail transport, the only one that holds a provider key.',
  emits: [],
  errors: [TenderNotFound, ReceiptMailLimitReached],
  dryRun: false,
  agent: { expose: false },
  legacy: ['POST /v1/receipt/payments'],
});
